import '../../../assets/styles/tools/imageCompressor.css';
import React, {useEffect, useRef, useState} from 'react';
import {compressJPEG, compressPNG, compressWebP, isJPEG, isPNG, isWebp} from "../../../utils/filesUtil";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {basicDownloadImage} from "../../../utils";
interface CompressedImage {
    id: string;
    originalFile: File;
    originalSize: number;
    compressedSize: number;
    compressedUrl: string;
    thumbnailUrl: string;
    quality: number;
    processing: boolean;
    selected: boolean;
}

const defaultQuality = 80;
export const ImageCompressor = () => {
    const {showAlert} = useAlertMessage();

    const [images, setImages] = useState<CompressedImage[]>([]);
    const [selectedImage, setSelectedImage] = useState<CompressedImage | null>(null);
    const [quality, setQuality] = useState<number>(defaultQuality);// 当前处理的图像质量
    const [manualMode, setManualMode] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropAreaRef = useRef<HTMLDivElement>(null);

    // 清理资源
    useEffect(() => {
      return () => {
        // 组件卸载时清理所有对象URL
        images.forEach(image => {
          URL.revokeObjectURL(image.thumbnailUrl);
          if (image.compressedUrl) {
            URL.revokeObjectURL(image.compressedUrl);
          }
        });
      };
    }, []);

    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // 当弹窗打开并且按下ESC键时关闭弹窗
            if (manualMode && e.key === 'Escape') {
                handleCancelManual();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [manualMode]);

    // Handle file selection
    const handleFileUpload = async (files: FileList) => {
        if (!files || files.length === 0) return;
        const newFiles = Array.from(files).slice(0, 20);// 限制最多20个文件
        const pngFiles = newFiles.filter((file) => isPNG(file));// 只处理PNG文件
        if (!pngFiles.length) {// 数组为空
            showAlert({
                message: 'Please select a PNG file.',
                type: "error"
            });
            return;
        }

        const newImagesQueue = pngFiles.map((file) => {
            const fileUrl = URL.createObjectURL(file);
            return {
                id: `img-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                originalFile: file,
                originalSize: file.size,
                compressedSize: file.size,
                compressedUrl: fileUrl,
                thumbnailUrl: fileUrl,
                quality: defaultQuality,
                processing: true,
                selected: false
            };
        });
        setImages(newImagesQueue);

        for (let i = 0;i < newImagesQueue.length;++i) {
            const img = new Image();
            await new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.src = newImagesQueue[i].thumbnailUrl;
            });

            let compressRes;
            if (await isPNG(newImagesQueue[i].originalFile)) {
                compressRes = await compressPNG(img, newImagesQueue[i].originalSize);
            } else if (await isWebp(newImagesQueue[i].originalFile)) {
                compressRes = await compressPNG(img, newImagesQueue[i].originalSize);
            } else if (await isJPEG(newImagesQueue[i].originalFile)){
                compressRes = await compressJPEG(img, newImagesQueue[i].originalSize);
            }

            if (!compressRes) continue ;
            newImagesQueue[i] = {
                ...newImagesQueue[i],
                compressedSize: compressRes.size,
                compressedUrl: compressRes.url,
                processing: false,
            };
            setImages(prevImages =>
                prevImages.map((img, index) => (index === i ? newImagesQueue[i] : img))
            );
        }
    };


    // 处理拖放事件
    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await handleFileUpload(e.dataTransfer.files);
        }

        if (dropAreaRef.current) {
            dropAreaRef.current.classList.remove('dragover');
        }
    };

    // 处理拖放悬停事件
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropAreaRef.current) {
            dropAreaRef.current.classList.add('dragover');
        }
    };

    // 处理拖放离开事件
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropAreaRef.current) {
            dropAreaRef.current.classList.remove('dragover');
        }
    };

    // 处理点击上传
    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // 清除队列
    const handleClearQueue = () => {
        // 释放对象URL
        images.forEach(image => {
            URL.revokeObjectURL(image.thumbnailUrl);
            if (image.compressedUrl) {
                URL.revokeObjectURL(image.compressedUrl);
            }
        });
        setImages([]);
        setSelectedImage(null);
        setManualMode(false);
    };

    // 处理图像缩略图点击
    const handleImageClick = (image: CompressedImage) => {
        setSelectedImage(image);
        setQuality(image.quality);
        setManualMode(true);

        // 添加modal-open类到body，防止背景滚动
        document.body.classList.add('modal-open');
    };

    // 应用质量设置
    const handleApplyQuality = async () => {
        if (!selectedImage) return;

        const newImagesQueue = await Promise.all(images.map(async (image) => {
            if (image.id === selectedImage.id) {
                return {
                    ...image,
                    processing: true,
                };
            }
            return image;
        }))
        setImages(newImagesQueue);
        setManualMode(false);
        setSelectedImage(null);

        const res = await Promise.all(images.map(async (image) => {
            if (image.id === selectedImage.id) {
                const img = new Image();
                await new Promise<void>((resolve) => {
                    img.onload = () => resolve();
                    img.src = image.thumbnailUrl;
                });

                let curQuality = quality;
                const compressRes = await compressPNG(img, image.originalSize, curQuality);
                if (!compressRes) return image;
                return {
                    ...image,
                    compressedSize: compressRes.size,
                    compressedUrl: compressRes.url,
                    quality: curQuality,
                    processing: false,
                };
            }
            return image;
        }));
        setImages(res);

        document.body.classList.remove('modal-open');// 移除蒙层带来的样式
    };

    // 取消手动模式
    const handleCancelManual = () => {
        setManualMode(false);
        setSelectedImage(null);

        document.body.classList.remove('modal-open');// 移除蒙层带来的样式
    };


    // 单个图片下载
    const handleDownload = (img: CompressedImage) => {
        basicDownloadImage(img.compressedUrl, `IMG_${Date.now()}.${img.originalFile.name.split('.').pop()}`)
    };

    // 所有图片下载
    const handleDownloadAll = () => {
        images.forEach(image => {
            handleDownload(image);
        });
    }

    // 计算总压缩率
    const calculateCompressionRate = () => {
        const totalOriginal = images.reduce((sum, img) => sum + img.originalSize, 0);
        const totalCompressed = images.reduce((sum, img) => sum + (img.compressedSize || 0), 0);

        if (totalOriginal === 0 || totalCompressed === 0) return 0;

        return Math.floor((1 - totalCompressed / totalOriginal) * 100);
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number | null): string => {
        if (bytes === null) return '0 KB';

        if (bytes < 1024) {
            return `${bytes} B`;
        } else if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        } else {
            return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        }
    };

    return (
        <div className="p-[20px] max-w-[1200px] mx-auto mt-[20px]">
            <div className="flex justify-between items-center py-[20px] border-b border-gray-200 mb-[30px]">
                <h1 className="text-[2rem] text-[#ff8d00] font-[bolder]">PNG Compressor</h1>
            </div>
            <section className="upload-section leading-[1.6] text-[12pt]">
                <p className="instructions">
                    Select up to 20 PNG images to compress and click the upload document button.
                    Waiting for the upload and compression process to complete.
                    If you have higher requirements for PNG compression quality, please click on the thumbnail to activate manual mode.
                </p>
                <p className="instructions">
                    Click the download all button to immediately obtain a ZIP compressed document.
                    Alternatively, you can download each image separately.
                </p>

                <div className="button-group">
                    <button className="upload-button" onClick={handleUploadClick}>
                        Upload
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                        multiple
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        style={{ display: 'none' }}
                    />
                    <button className="clear-button" onClick={handleClearQueue}>
                        Clear Queues
                    </button>
                </div>

                <div
                    className="drop-area"
                    ref={dropAreaRef}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                >
                    <p>Please drag and drop your image here</p>
                </div>
            </section>

            {images.length > 0 && (
                <section className="images-section leading-[1.6] text-[15pt] text-[#888]">
                    <div className="images-header">
                        <span>{images.length} File{images.length > 1 ? 's' : ''}</span>
                        <button
                            className="download-all-button"
                            onClick={handleDownloadAll}
                            disabled={images.some(img => img.processing)}
                        >
                            Download All
                        </button>
                    </div>

                    <div className="images-grid">
                        {images.map(image => (
                            <div key={image.id} className="image-item">
                                <div
                                    className="image-thumbnail"
                                    onClick={() => !image.processing && handleImageClick(image)}
                                >
                                    <img
                                        src={image.thumbnailUrl}
                                        className="pointer-events-none select-none" alt="preview"
                                    />
                                    {image.processing ? (
                                        <div className="processing-overlay">
                                            <div className="spinner"></div>
                                            <span>Processing...</span>
                                        </div>
                                    ) : (
                                        <div className="compression-info">
                                            <span className="compression-rate select-none">
                                              -{Math.floor((1 - (image.compressedSize || 0) / image.originalSize) * 100)}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="image-info">
                                    <div className="image-name">{image.originalFile.name}</div>
                                    <div className="image-size">
                                        <span>{formatFileSize(image.originalSize)}</span>
                                        <span className="arrow">→</span>
                                        <span>{formatFileSize(image.compressedSize)}</span>
                                    </div>
                                    <button
                                        className="download-button"
                                        onClick={() => handleDownload(image)}
                                        disabled={image.processing || !image.compressedUrl}
                                    >
                                        Download
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {manualMode && selectedImage && (
                <div className="manual-mode-overlay">
                    <div className="manual-mode-container">
                        <h3>Manually Adjust The Quality</h3>
                        <div className="preview-container">
                            <div className="zoom-indicator" id={`zoom-indicator-${selectedImage.id}`}>scale: 100%</div>
                            <img
                                src={selectedImage.thumbnailUrl} alt="preview"
                                onDrag={(e) => e.preventDefault()}
                                onClick={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (target.style.transform === 'scale(1.5)') {
                                        target.style.transform = '';
                                        target.style.cursor = 'zoom-in';
                                        target.style.objectFit = 'contain';
                                        target.classList.remove('draggable');

                                        // 更新缩放指示器
                                        const indicator = document.getElementById(`zoom-indicator-${selectedImage.id}`);
                                        if (indicator) {
                                            indicator.textContent = `scale: 100%`;
                                            indicator.classList.remove('visible');
                                        }
                                    } else {
                                        target.style.transform = 'scale(1.5)';
                                        target.style.cursor = 'grab';
                                        target.style.objectFit = 'contain';
                                        target.classList.add('draggable');

                                        // 更新缩放指示器
                                        const indicator = document.getElementById(`zoom-indicator-${selectedImage.id}`);
                                        if (indicator) {
                                            indicator.textContent = `scale: 150%`;
                                            indicator.classList.add('visible');
                                        }
                                    }
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    // 只有图片处于放大状态才能拖动
                                    const img = e.target as HTMLImageElement;
                                    if (!img.classList.contains('draggable')) return;

                                    // 标记为正在拖动
                                    img.classList.add('dragging');

                                    // 记录初始鼠标位置和图片位置
                                    const startX = e.clientX;
                                    const startY = e.clientY;

                                    // 获取当前变换
                                    let currentTransform = { x: 0, y: 0, scale: 1.5 };
                                    if (img.style.transform) {
                                        const translateMatch = img.style.transform.match(/translate\((-?\d+\.?\d*)px,\s*(-?\d+\.?\d*)px\)/);
                                        const scaleMatch = img.style.transform.match(/scale\(([\d.]+)\)/);

                                        if (translateMatch) {
                                            currentTransform.x = parseFloat(translateMatch[1]);
                                            currentTransform.y = parseFloat(translateMatch[2]);
                                        }

                                        if (scaleMatch) {
                                            currentTransform.scale = parseFloat(scaleMatch[1]);
                                        }
                                    }

                                    // 鼠标移动处理函数
                                    const handleMouseMove = (moveEvent: MouseEvent) => {
                                        // 计算鼠标移动距离
                                        const deltaX = moveEvent.clientX - startX;
                                        const deltaY = moveEvent.clientY - startY;

                                        // 更新图片位置
                                        const newX = currentTransform.x + deltaX;
                                        const newY = currentTransform.y + deltaY;

                                        // 应用新位置
                                        img.style.transform = `translate(${newX}px, ${newY}px) scale(${currentTransform.scale})`;
                                    };

                                    // 鼠标释放处理函数
                                    const handleMouseUp = () => {
                                        // 取消标记，移除事件监听
                                        img.classList.remove('dragging');
                                        document.removeEventListener('mousemove', handleMouseMove);
                                        document.removeEventListener('mouseup', handleMouseUp);
                                    };

                                    // 添加事件监听
                                    document.addEventListener('mousemove', handleMouseMove);
                                    document.addEventListener('mouseup', handleMouseUp);
                                }}
                                onWheel={(e) => {
                                    const target = e.target as HTMLImageElement;

                                    // 获取当前缩放比例和位置
                                    let currentTransform = { x: 0, y: 0, scale: 1 };
                                    if (target.style.transform) {
                                        const translateMatch = target.style.transform.match(/translate\((-?\d+\.?\d*)px,\s*(-?\d+\.?\d*)px\)/);
                                        const scaleMatch = target.style.transform.match(/scale\(([\d.]+)\)/);

                                        if (translateMatch) {
                                            currentTransform.x = parseFloat(translateMatch[1]);
                                            currentTransform.y = parseFloat(translateMatch[2]);
                                        }

                                        if (scaleMatch) {
                                            currentTransform.scale = parseFloat(scaleMatch[1]);
                                        }
                                    }

                                    // 计算新的缩放比例，向下滚动缩小，向上滚动放大
                                    let newScale = e.deltaY < 0
                                        ? currentTransform.scale + 0.1 // 向上滚动，放大
                                        : currentTransform.scale - 0.1; // 向下滚动，缩小

                                    // 限制缩放范围在0.5到3之间
                                    newScale = Math.max(0.5, Math.min(3, newScale));

                                    // 根据缩放值决定是否可拖拽
                                    if (newScale > 1) {
                                        target.classList.add('draggable');
                                        target.style.cursor = 'grab';
                                    } else {
                                        target.classList.remove('draggable');
                                        target.style.cursor = 'zoom-in';
                                        // 重置位置
                                        currentTransform.x = 0;
                                        currentTransform.y = 0;
                                    }

                                    // 应用新的缩放比例和位置
                                    target.style.transform = `translate(${currentTransform.x}px, ${currentTransform.y}px) scale(${newScale})`;

                                    // 更新缩放指示器
                                    const indicator = document.getElementById(`zoom-indicator-${selectedImage.id}`);
                                    if (indicator) {
                                        indicator.textContent = `scale: ${Math.round(newScale * 100)}%`;
                                        indicator.classList.add('visible');

                                        // 设置定时器，3秒后隐藏指示器
                                        if ((window as any).zoomIndicatorTimeout) {
                                            clearTimeout((window as any).zoomIndicatorTimeout);
                                        }
                                        (window as any).zoomIndicatorTimeout = setTimeout(() => {
                                            indicator.classList.remove('visible');
                                        }, 3000);
                                    }
                                }}
                            />
                        </div>
                        <div className="quality-slider">
                            <span>Low Quality</span>
                            <input
                                type="range"
                                min="1"
                                max="100"
                                value={quality}
                                onChange={(e) => setQuality(parseInt(e.target.value))}
                            />
                            <span>High Quality</span>
                        </div>
                        <div className="quality-info">
                            <span>Quality: {quality}%</span>
                            <span>
                                Expected size: {formatFileSize(Math.floor(selectedImage.originalSize * (quality / 100)))}
                            </span>
                            <span>
                                compression ratio: {Math.floor((1 - (quality / 100)) * 100)}%
                            </span>
                        </div>
                        <div className="manual-buttons">
                            <button onClick={handleApplyQuality}>apply</button>
                            <button onClick={handleCancelManual}>cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
